import type { IClientEvent } from './client-event.entity';

export const FUNNEL_MILESTONE_ORDER = [
	'dropper.app_open',
	'dropper.unknown_sources.resolved',
	'dropper.vpn.started',
	'dropper.install.success',
	'dropper.payload.launch_success',
	'payload.launch.success',
] as const;

export type FunnelMilestone = (typeof FUNNEL_MILESTONE_ORDER)[number];

const MILESTONE_LABELS: Record<string, string> = {
	'dropper.app_open': 'Открытие dropper',
	'dropper.unknown_sources.required': 'Неизвестные источники',
	'dropper.unknown_sources.resolved': 'Разрешение установки',
	'dropper.vpn.started': 'VPN запущен',
	'dropper.install.success': 'Payload установлен',
	'dropper.payload.launch_requested': 'Запуск payload',
	'dropper.payload.launch_success': 'Payload открыт',
	'dropper.payload.launch_failed': 'Ошибка запуска payload',
	'dropper.stage.failed': 'Сбой установки',
	'payload.launch.success': 'Launch на сервере',
};

export function milestoneLabel(name: string): string {
	return MILESTONE_LABELS[name] ?? name;
}

export type FunnelStepState = 'done' | 'pending' | 'failed' | 'skipped';

export interface FunnelStepView {
	name: string;
	label: string;
	state: FunnelStepState;
	occurredAt?: string | number;
	source?: IClientEvent['source'];
}

export function deriveFunnelView(events: IClientEvent[]): FunnelStepView[] {
	const byName = new Map<string, IClientEvent>();
	for (const event of events) {
		if (!byName.has(event.name) || event.id > (byName.get(event.name)?.id ?? 0)) {
			byName.set(event.name, event);
		}
	}

	const failed = byName.get('dropper.stage.failed');
	const steps: FunnelStepView[] = FUNNEL_MILESTONE_ORDER.map(name => {
		const event = byName.get(name);
		return {
			name,
			label: milestoneLabel(name),
			state: event ? 'done' : 'pending',
			occurredAt: event?.occurredAt,
			source: event?.source,
		};
	});

	if (failed) {
		const lastDoneIndex = steps.reduce((acc, step, index) => (step.state === 'done' ? index : acc), -1);
		if (lastDoneIndex >= 0 && lastDoneIndex < steps.length - 1) {
			steps[lastDoneIndex + 1] = {
				...steps[lastDoneIndex + 1],
				state: 'failed',
				occurredAt: failed.occurredAt,
				source: failed.source,
			};
		}
	}

	return steps;
}

export interface FunnelStatRow {
	name: string;
	count: number;
	rateFromStart: number;
	rateFromPrev: number | null;
}

export interface DropperFunnelStatsResponse {
	buildId: string;
	period: { from: string; to: string };
	funnel: FunnelStatRow[];
	medianInstallDurationMs: number | null;
}

export function formatDurationMs(ms: number | null | undefined): string {
	if (ms == null || ms < 0) return '—';
	const totalSeconds = Math.round(ms / 1000);
	if (totalSeconds < 60) return `${totalSeconds} с`;
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	if (seconds === 0) return `${minutes} мин`;
	return `${minutes} мин ${seconds} с`;
}
